/**
 * shareImage — hand a generated image Blob to the platform share sheet.
 *
 * Native (Capacitor): write the PNG to the Filesystem cache and share the file
 * URI, mirroring the proven calendarInvite.ts pattern. Web: prefer Web Share
 * Level 2 (`navigator.share({ files })`) and fall back to a plain download so
 * the button always does something in a browser/dev.
 */
import { Capacitor } from "@capacitor/core";

export type ShareImageOutcome = "shared" | "downloaded" | "cancelled";

export interface ShareImageInput {
  blob: Blob;
  /** File name incl. extension, e.g. "nexus-dex-trade.png". */
  fileName: string;
  title?: string;
  text?: string;
  dialogTitle?: string;
}

export async function shareImageBlob(input: ShareImageInput): Promise<ShareImageOutcome> {
  const { blob, fileName, title, text, dialogTitle } = input;

  if (Capacitor.isNativePlatform()) {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import("@capacitor/filesystem"),
      import("@capacitor/share"),
    ]);
    const base64 = await blobToBase64(blob);
    // No `encoding` → Capacitor writes the base64 payload as raw binary.
    const written = await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Cache,
    });
    try {
      await Share.share({ title, text, files: [written.uri], dialogTitle });
      return "shared";
    } catch {
      // User dismissed the share sheet, or no target accepted it.
      return "cancelled";
    }
  }

  // Web: try the file-aware share sheet first.
  const file = new File([blob], fileName, { type: blob.type || "image/png" });
  const nav = navigator as Navigator & {
    canShare?: (data?: ShareData) => boolean;
    share?: (data?: ShareData) => Promise<void>;
  };
  if (typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title, text });
      return "shared";
    } catch {
      // Fall through to download (covers AbortError + unsupported targets).
    }
  }

  triggerDownload(blob, fileName);
  return "downloaded";
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result);
      // Strip the "data:*/*;base64," prefix — Filesystem wants raw base64.
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("blob read failed"));
    reader.readAsDataURL(blob);
  });
}
